/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useState } from "react";
import { ResultActions } from "@/components/tools/result-actions";

const defaultRates = {
  socialSecurity: 6.2,
  medicare: 1.45,
  futa: 0.6,
  suta: 3.0,
  workersComp: 8.0,
  generalLiability: 3.0,
  healthInsurance: 500,
  retirement: 0,
  pto: 0,
};

export function LaborBurdenCalculatorClient() {
  const [hourlyWage, setHourlyWage] = useState("");
  const [hoursPerWeek, setHoursPerWeek] = useState("40");
  const [weeksPerYear, setWeeksPerYear] = useState("52");
  const [socialSecurity, setSocialSecurity] = useState(String(defaultRates.socialSecurity));
  const [medicare, setMedicare] = useState(String(defaultRates.medicare));
  const [futa, setFuta] = useState(String(defaultRates.futa));
  const [suta, setSuta] = useState(String(defaultRates.suta));
  const [workersComp, setWorkersComp] = useState(String(defaultRates.workersComp));
  const [generalLiability, setGeneralLiability] = useState(String(defaultRates.generalLiability));
  const [healthInsuranceMonthly, setHealthInsuranceMonthly] = useState(String(defaultRates.healthInsurance));
  const [retirementMatch, setRetirementMatch] = useState(String(defaultRates.retirement));
  const [ptoDays, setPtoDays] = useState(String(defaultRates.pto));

  const wage = parseFloat(hourlyWage) || 0;
  const hpw = parseFloat(hoursPerWeek) || 40;
  const wpy = parseFloat(weeksPerYear) || 52;
  const annualHours = hpw * wpy;
  const annualWage = wage * annualHours;

  const ss = (parseFloat(socialSecurity) || 0) / 100;
  const med = (parseFloat(medicare) || 0) / 100;
  const futaRate = (parseFloat(futa) || 0) / 100;
  const sutaRate = (parseFloat(suta) || 0) / 100;
  const wcRate = (parseFloat(workersComp) || 0) / 100;
  const glRate = (parseFloat(generalLiability) || 0) / 100;
  const healthAnnual = (parseFloat(healthInsuranceMonthly) || 0) * 12;
  const retireRate = (parseFloat(retirementMatch) || 0) / 100;
  const ptoD = parseFloat(ptoDays) || 0;

  // FUTA caps at $7,000 wages
  const futaCost = Math.min(annualWage, 7000) * futaRate;
  // SUTA typically caps at ~$10,000 (varies by state)
  const sutaCost = Math.min(annualWage, 10000) * sutaRate;
  const ssCost = annualWage * ss;
  const medCost = annualWage * med;
  const wcCost = annualWage * wcRate;
  const glCost = annualWage * glRate;
  const retireCost = annualWage * retireRate;
  const ptoCost = ptoD * wage * hpw / 5; // daily rate * days

  const totalBurden = ssCost + medCost + futaCost + sutaCost + wcCost + glCost + healthAnnual + retireCost + ptoCost;
  const totalCost = annualWage + totalBurden;
  const burdenRate = annualWage > 0 ? (totalBurden / annualWage) * 100 : 0;
  const fullyBurdenedHourly = annualHours > 0 ? totalCost / annualHours : 0;

  const hasResult = wage > 0;

  const fmt = (n: number) => n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const breakdownItems = [
    { label: "Social Security (6.2%)", value: ssCost },
    { label: "Medicare (1.45%)", value: medCost },
    { label: "FUTA", value: futaCost },
    { label: "SUTA", value: sutaCost },
    { label: "Workers' Comp", value: wcCost },
    { label: "General Liability", value: glCost },
    { label: "Health Insurance", value: healthAnnual },
    { label: "Retirement Match", value: retireCost },
    { label: "PTO Cost", value: ptoCost },
  ].filter((item) => item.value > 0);

  return (
    <div className="mt-8 rounded-lg card-premium p-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <label className="block text-sm font-medium text-foreground">Hourly Wage ($)</label>
          <input
            type="number"
            value={hourlyWage}
            onChange={(e) => setHourlyWage(e.target.value)}
            placeholder="25.00"
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">Hours / Week</label>
          <input
            type="number"
            value={hoursPerWeek}
            onChange={(e) => setHoursPerWeek(e.target.value)}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">Weeks / Year</label>
          <input
            type="number"
            value={weeksPerYear}
            onChange={(e) => setWeeksPerYear(e.target.value)}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
          />
        </div>
      </div>

      <details className="mt-6" open>
        <summary className="cursor-pointer text-sm font-semibold text-foreground">Payroll Taxes & Insurance Rates</summary>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <div>
            <label className="block text-sm font-medium text-foreground">Social Security %</label>
            <input type="number" step="0.01" value={socialSecurity} onChange={(e) => setSocialSecurity(e.target.value)} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground">Medicare %</label>
            <input type="number" step="0.01" value={medicare} onChange={(e) => setMedicare(e.target.value)} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground">FUTA %</label>
            <input type="number" step="0.01" value={futa} onChange={(e) => setFuta(e.target.value)} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground">SUTA %</label>
            <input type="number" step="0.01" value={suta} onChange={(e) => setSuta(e.target.value)} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground">Workers&apos; Comp %</label>
            <input type="number" step="0.01" value={workersComp} onChange={(e) => setWorkersComp(e.target.value)} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground">General Liability %</label>
            <input type="number" step="0.01" value={generalLiability} onChange={(e) => setGeneralLiability(e.target.value)} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
          </div>
        </div>
      </details>

      <details className="mt-4">
        <summary className="cursor-pointer text-sm font-semibold text-foreground">Benefits (Optional)</summary>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <div>
            <label className="block text-sm font-medium text-foreground">Health Insurance ($/month)</label>
            <input type="number" value={healthInsuranceMonthly} onChange={(e) => setHealthInsuranceMonthly(e.target.value)} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground">Retirement Match %</label>
            <input type="number" step="0.01" value={retirementMatch} onChange={(e) => setRetirementMatch(e.target.value)} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground">PTO Days / Year</label>
            <input type="number" value={ptoDays} onChange={(e) => setPtoDays(e.target.value)} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
          </div>
        </div>
      </details>

      {hasResult && (
        <>
          <div className="mt-6 grid gap-4 rounded-lg bg-muted/50 p-4 sm:grid-cols-4">
            <div>
              <div className="text-sm text-muted-foreground">Base Hourly</div>
              <div className="text-2xl font-bold text-foreground">${fmt(wage)}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Fully Burdened Hourly</div>
              <div className="text-2xl font-bold text-secondary">${fmt(fullyBurdenedHourly)}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Burden Rate</div>
              <div className="text-2xl font-bold text-secondary">{fmt(burdenRate)}%</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Annual Total Cost</div>
              <div className="text-2xl font-bold text-foreground">${fmt(totalCost)}</div>
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-border p-4">
            <h3 className="mb-3 text-sm font-semibold text-foreground">Cost Breakdown (Annual)</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Base Wages</span>
                <span className="font-medium text-foreground">${fmt(annualWage)}</span>
              </div>
              {breakdownItems.map((item) => (
                <div key={item.label} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{item.label}</span>
                  <span className="font-medium text-foreground">${fmt(item.value)}</span>
                </div>
              ))}
              <div className="flex justify-between border-t border-border pt-2 text-sm font-semibold">
                <span className="text-foreground">Total Annual Cost</span>
                <span className="text-secondary">${fmt(totalCost)}</span>
              </div>
            </div>
          </div>

          <ResultActions
            toolSlug="labor-burden-calculator"
            inputs={{ hourlyWage, hoursPerWeek, weeksPerYear, socialSecurity, medicare, futa, suta, workersComp, generalLiability, healthInsuranceMonthly, retirementMatch, ptoDays }}
            outputs={{ fullyBurdenedHourly, totalCost, totalBurden, burdenRate: (totalBurden / annualWage * 100) }}
          />
        </>
      )}
    </div>
  );
}
